/**
 * useLayerLabels.ts — أسماء الطبقات بلغة الواجهة، مع احترام تخصيص الأدمن.
 *
 * القاعدة: إن غيّر الأدمن اسم الطبقة (label_ar/label_en في قاعدة البيانات) عن الافتراضي
 * المكتوب في الكود، يفوز اسم الأدمن؛ وإلا نأخذ الترجمة من القاموس. بهذا يبقى تخصيص
 * الأدمن فعّالاً في كلا اللغتين، ولا نضطر لتكرار الأسماء العربية في مكانين.
 */
import { WEATHER_LAYER_CONFIGS } from '../config/weatherLayers';
import { getLayerOverride } from '../config/remoteConfig';
import type { LayerKey } from '../store/types';
import { useTranslation } from './LanguageContext';

export function useLayerLabels() {
    const { t, lang } = useTranslation();

    /** اسم الأدمن المخصّص لهذه اللغة، أو null إن لم يخصّص شيئاً (أو طابق الافتراضي). */
    const adminLabel = (id: LayerKey): string | null => {
        const override = getLayerOverride(id);
        if (!override) return null;
        const custom = lang === 'ar' ? override.label_ar : override.label_en;
        if (!custom) return null;
        const base = WEATHER_LAYER_CONFIGS[id];
        const codeDefault = lang === 'ar' ? base.labelAr : base.labelEn;
        return custom !== codeDefault ? custom : null;
    };

    return {
        /** الاسم الكامل للطبقة (شريط الأدوات، لوحة الحالة، الأزرار العائمة). */
        layerName: (id: LayerKey): string => adminLabel(id) ?? t(`layer.${id}`),
        /** اسم المجموعة داخل قائمة الطبقات (مختصر: «الضغط» لا «الضغط الجوي»). */
        groupName: (parentId: LayerKey): string => adminLabel(parentId) ?? t(`layerGroup.${parentId}`),
        /** اسم المتغيّر داخل المجموعة («فعلي»، «النسبية»…). */
        variantName: (id: LayerKey): string => adminLabel(id) ?? t(`layerVariant.${id}`),
        /** وصف الطبقة (لا يخصّصه الأدمن حالياً). */
        layerDesc: (id: LayerKey): string => t(`layerDesc.${id}`),
    };
}
